import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { 
  Flame, Thermometer, Snowflake, Trash2, Calendar, Clock, 
  MessageSquare, Phone, Mail, User, Building2, Plus, Check,
  ChevronDown, ChevronUp, Send, X, Edit2, Save, Bell
} from 'lucide-react';
import axios from 'axios';
import { BACKEND_URL } from '../../utils/api';

const API = BACKEND_URL;

// Lead temperature configuration
const TEMPERATURE_CONFIG = {
  hot: { 
    label: 'Hot', 
    icon: Flame, 
    color: 'bg-red-500 text-white', 
    bgLight: 'bg-red-50 border-red-200',
    description: 'Ready to close - follow up TODAY'
  },
  warm: { 
    label: 'Warm', 
    icon: Thermometer, 
    color: 'bg-orange-500 text-white', 
    bgLight: 'bg-orange-50 border-orange-200',
    description: 'Interested - follow up this week'
  },
  cold: { 
    label: 'Cold', 
    icon: Snowflake, 
    color: 'bg-blue-500 text-white', 
    bgLight: 'bg-blue-50 border-blue-200',
    description: 'Not ready yet - nurture over time'
  },
  dead: { 
    label: 'Dead', 
    icon: Trash2, 
    color: 'bg-slate-400 text-white', 
    bgLight: 'bg-slate-50 border-slate-200',
    description: 'Not a fit - archive'
  },
};

const TEMPERATURE_ORDER = ['hot', 'warm', 'cold', 'dead'];

export default function LeadCRMPanel({ lead, onUpdate, getAuthHeaders }) {
  const [temperature, setTemperature] = useState(lead?.temperature || 'warm');
  const [followUpDate, setFollowUpDate] = useState(lead?.follow_up_date || '');
  const [followUpTime, setFollowUpTime] = useState(lead?.follow_up_time || '09:00');
  const [notes, setNotes] = useState(lead?.crm_notes || []);
  const [newNote, setNewNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(true);

  const handleSaveTemperature = async (newTemp) => {
    setTemperature(newTemp);
    try {
      await axios.put(`${API}/api/admin/leads/${lead.collection}/${lead.id}/crm`, {
        temperature: newTemp
      }, { headers: getAuthHeaders() });
      if (onUpdate) onUpdate({ ...lead, temperature: newTemp });
    } catch (err) {
      console.error('Failed to update temperature:', err);
    }
  };

  const handleSaveFollowUp = async () => {
    if (!followUpDate) return;
    setSaving(true);
    try {
      await axios.put(`${API}/api/admin/leads/${lead.collection}/${lead.id}/crm`, {
        follow_up_date: followUpDate,
        follow_up_time: followUpTime
      }, { headers: getAuthHeaders() });
      if (onUpdate) onUpdate({ ...lead, follow_up_date: followUpDate, follow_up_time: followUpTime });
    } catch (err) {
      console.error('Failed to save follow-up:', err);
    }
    setSaving(false);
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    setSaving(true);
    const noteObj = {
      text: newNote,
      created_at: new Date().toISOString(),
      type: 'note'
    };
    try {
      await axios.post(`${API}/api/admin/leads/${lead.collection}/${lead.id}/notes`, {
        note: newNote
      }, { headers: getAuthHeaders() });
      setNotes([noteObj, ...notes]);
      setNewNote('');
      if (onUpdate) onUpdate({ ...lead, crm_notes: [noteObj, ...notes] });
    } catch (err) {
      console.error('Failed to add note:', err);
    }
    setSaving(false);
  };

  const handleLogActivity = async (activityType) => {
    const activityText = {
      call: 'Made a phone call',
      email: 'Sent an email',
      meeting: 'Had a meeting',
    }[activityType] || activityType;
    
    setSaving(true);
    const noteObj = {
      text: activityText,
      created_at: new Date().toISOString(),
      type: activityType
    };
    try {
      await axios.post(`${API}/api/admin/leads/${lead.collection}/${lead.id}/notes`, {
        note: activityText,
        type: activityType
      }, { headers: getAuthHeaders() });
      setNotes([noteObj, ...notes]);
      if (onUpdate) onUpdate({ ...lead, crm_notes: [noteObj, ...notes] });
    } catch (err) {
      console.error('Failed to log activity:', err);
    }
    setSaving(false);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', { 
        month: 'short', day: 'numeric', year: 'numeric', 
        hour: 'numeric', minute: '2-digit' 
      });
    } catch {
      return dateStr;
    }
  };

  const tempConfig = TEMPERATURE_CONFIG[temperature] || TEMPERATURE_CONFIG.warm;
  const TempIcon = tempConfig.icon;

  return (
    <div className={`border rounded-lg ${tempConfig.bgLight} p-4`} data-testid="lead-crm-panel">
      {/* Temperature Rating */}
      <div className="mb-4">
        <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 block">
          Lead Temperature
        </label>
        <div className="flex gap-2 flex-wrap">
          {TEMPERATURE_ORDER.map(temp => {
            const cfg = TEMPERATURE_CONFIG[temp];
            const Icon = cfg.icon;
            const isActive = temperature === temp;
            return (
              <button
                key={temp}
                onClick={() => handleSaveTemperature(temp)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                  isActive ? cfg.color : 'bg-white border border-slate-200 text-slate-600 hover:border-slate-300'
                }`}
                title={cfg.description}
                data-testid={`temp-${temp}`}
              >
                <Icon className="h-4 w-4" />
                {cfg.label}
              </button>
            );
          })}
        </div>
        <p className="text-xs text-slate-500 mt-1">{tempConfig.description}</p>
      </div>

      {/* Follow-up Scheduler */}
      <div className="mb-4">
        <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 block">
          Schedule Follow-up
        </label>
        <div className="flex gap-2 items-end flex-wrap">
          <div>
            <Input
              type="date"
              value={followUpDate}
              onChange={e => setFollowUpDate(e.target.value)}
              className="h-9 text-sm w-40"
              data-testid="followup-date"
            />
          </div>
          <div>
            <Input
              type="time"
              value={followUpTime}
              onChange={e => setFollowUpTime(e.target.value)}
              className="h-9 text-sm w-28"
              data-testid="followup-time"
            />
          </div>
          <Button 
            size="sm" 
            onClick={handleSaveFollowUp}
            disabled={!followUpDate || saving}
            className="h-9"
          >
            <Bell className="h-3 w-3 mr-1" /> Set Reminder
          </Button>
        </div>
        {lead?.follow_up_date && (
          <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
            <Check className="h-3 w-3" /> Follow-up set for {formatDate(lead.follow_up_date + 'T' + (lead.follow_up_time || '09:00'))}
          </p>
        )}
      </div>

      {/* Quick Log Activity */}
      <div className="mb-4">
        <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 block">
          Log Activity
        </label>
        <div className="flex gap-2">
          <Button 
            size="sm" 
            variant="outline" 
            onClick={() => handleLogActivity('call')}
            disabled={saving}
            className="h-8"
          >
            <Phone className="h-3 w-3 mr-1" /> Log Call
          </Button>
          <Button 
            size="sm" 
            variant="outline" 
            onClick={() => handleLogActivity('email')}
            disabled={saving}
            className="h-8"
          >
            <Mail className="h-3 w-3 mr-1" /> Log Email
          </Button>
          <Button 
            size="sm" 
            variant="outline" 
            onClick={() => handleLogActivity('meeting')}
            disabled={saving}
            className="h-8"
          >
            <User className="h-3 w-3 mr-1" /> Log Meeting
          </Button>
        </div>
      </div>

      {/* Notes/Activity Timeline */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
            Notes & Activity
          </label>
          <button 
            onClick={() => setExpanded(!expanded)} 
            className="text-xs text-slate-400 hover:text-slate-600"
          >
            {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
        </div>
        
        {expanded && (
          <>
            {/* Add Note Input */}
            <div className="flex gap-2 mb-3">
              <Input
                placeholder="Add a note..."
                value={newNote}
                onChange={e => setNewNote(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleAddNote()}
                className="h-9 text-sm flex-1"
                data-testid="add-note-input"
              />
              <Button 
                size="sm" 
                onClick={handleAddNote}
                disabled={!newNote.trim() || saving}
                className="h-9"
              >
                <Plus className="h-3 w-3 mr-1" /> Add
              </Button>
            </div>

            {/* Notes List */}
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {notes.length === 0 ? (
                <p className="text-xs text-slate-400 italic py-2">No notes yet</p>
              ) : (
                notes.map((note, i) => (
                  <div 
                    key={i} 
                    className={`text-sm p-2 rounded-lg border ${
                      note.type === 'call' ? 'bg-green-50 border-green-200' :
                      note.type === 'email' ? 'bg-blue-50 border-blue-200' :
                      note.type === 'meeting' ? 'bg-purple-50 border-purple-200' :
                      'bg-white border-slate-200'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-slate-700 flex-1">{note.text}</p>
                      <span className="text-[10px] text-slate-400 whitespace-nowrap">
                        {formatDate(note.created_at)}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
